import "mapbox-gl/dist/mapbox-gl.css";

import { MeetingRoom, Numbers, ShowChart } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import * as turf from "@turf/turf";
import React from "react";
import { useTranslation } from "react-i18next";
import Map, { type MapRef, NavigationControl } from "react-map-gl/mapbox";
import { useNavigate } from "react-router-dom";

import { useStoreBranding } from "@/hooks/useStoreBranding";
import { paths } from "@/routes/paths";
import { Customer, Store, StoreService } from "@/services/features/StoreService";
import { formatInteger } from "@/utils/number";

import { StoreHealthCache } from "./commandCenter.types";
import { CLUSTER_MAX_ZOOM, SELECTED_STORE_ZOOM } from "./commandCenterClustering";
import { getMonitoringHealthStatus } from "./commandCenterHealth";
import { CommandCenterMapMarkers, type StackInteraction } from "./CommandCenterMapMarkers";

type Props = {
  customers: Customer[];
  stores: Store[];
  selectedCustomerId: number | null;
  selectedStoreId: number | null;
  healthByStoreId: Record<number, StoreHealthCache>;
  onSelectStore: (id: number) => void;
  layoutVersion: number;
  expanded: boolean;
};

const token = import.meta.env.VITE_MAPBOX_TOKEN;

type StoreMapMarkerProps = {
  store: Store;
  healthStatus: ReturnType<typeof getMonitoringHealthStatus>;
  isDimmed: boolean;
  fittingRoomCount: number | null | undefined;
  avatar?: string;
  color: string;
  customerName?: string;
  expanded: boolean;
  onSelectStore: (id: number) => void;
  loadFittingRoomCount: (id: number) => Promise<void>;
  interaction?: StackInteraction;
};

const StoreMapMarker = React.memo(function StoreMapMarker({
  store,
  healthStatus,
  isDimmed,
  fittingRoomCount,
  avatar,
  color,
  customerName,
  expanded,
  onSelectStore,
  loadFittingRoomCount,
  interaction,
}: StoreMapMarkerProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = i18n.resolvedLanguage || i18n.language || "en";
  const markerSize = expanded ? 32 : 24;
  const markerAvatarSize = expanded ? 30 : 22;
  const markerStatusSize = expanded ? 9 : 7;
  const healthColor = healthStatus === "toWatch" ? "warning.main" : "error.main";
  return (
    <Tooltip
      title={
        <Stack spacing={1} sx={{ minWidth: 220 }}>
          <Typography variant="h6" component="div" fontWeight={700} lineHeight={1.2}>
            {store.name}
          </Typography>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ pb: 1 }}>
            <Avatar
              src={avatar}
              alt=""
              sx={{
                width: 20,
                height: 20,
                flexShrink: 0,
                border: `1px solid ${color}`,
                "& .MuiAvatar-img": { objectFit: "contain" },
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {customerName || t("commandCenter.notAvailable")}
            </Typography>
          </Stack>
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            <Chip
              size="small"
              icon={
                <Box
                  component="img"
                  src="/images/icons/crave_icon.svg"
                  alt=""
                  sx={{ width: 18, height: 18 }}
                />
              }
              label={formatInteger(store.id, locale)}
            />
            <Chip
              size="small"
              icon={<Numbers />}
              label={store.externalId || t("commandCenter.notAvailable")}
            />
            <Chip
              size="small"
              icon={<MeetingRoom />}
              label={
                fittingRoomCount === undefined ? (
                  <Skeleton width="2ch" height={16} aria-label={t("common.loading")} />
                ) : fittingRoomCount === null ? (
                  t("commandCenter.notAvailable")
                ) : (
                  formatInteger(fittingRoomCount, locale)
                )
              }
            />
          </Stack>
          <Divider />
          <Button
            variant="text"
            size="small"
            startIcon={<ShowChart />}
            onClick={(event) => {
              event.stopPropagation();
              void navigate(`${paths.storeInsights.performance}?storeId=${store.id}`);
            }}
            sx={{ alignSelf: "stretch", justifyContent: "flex-start" }}
          >
            {t("commandCenter.actions.viewStorePerformance")}
          </Button>
        </Stack>
      }
      onOpen={() => void loadFittingRoomCount(store.id)}
      arrow
      placement="top"
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: "common.white",
            color: "text.primary",
            px: 1.5,
            py: 1.25,
            borderRadius: 1.5,
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
          },
        },
        arrow: { sx: { color: "common.white" } },
        popper: {
          ...interaction,
          modifiers: [
            {
              name: "flip",
              enabled: true,
              options: { fallbackPlacements: ["bottom"] },
            },
            { name: "preventOverflow", enabled: true, options: { padding: 8 } },
          ],
        },
      }}
    >
      <Box
        component="button"
        type="button"
        aria-label={store.name}
        onClick={(event) => {
          event.stopPropagation();
          onSelectStore(store.id);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onSelectStore(store.id);
          }
        }}
        sx={{
          p: interaction ? 0 : "1px",
          width: markerSize,
          height: markerSize,
          border: 0,
          borderRadius: "50%",
          bgcolor: "background.paper",
          position: "relative",
          cursor: "pointer",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            transform: "scale(1.2)",
            "& > .MuiAvatar-root": { borderWidth: 2 },
            "& .command-center-pin-shadow": {
              transform: "scale(1.08)",
              opacity: 1,
            },
          },
        }}
      >
        <Box
          className="command-center-pin-shadow"
          sx={{
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            bgcolor: isDimmed ? "grey.500" : color,
            opacity: 0.2,
            transform: "scale(1)",
            transition: "all 0.2s ease-in-out",
          }}
        />
        <Avatar
          src={avatar}
          sx={{
            width: interaction ? markerSize : markerAvatarSize,
            height: interaction ? markerSize : markerAvatarSize,
            bgcolor: "background.paper",
            color: "text.secondary",
            border: `1px solid ${color}`,
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            position: "relative",
            zIndex: 1,
            filter: isDimmed ? "grayscale(1)" : "none",
            opacity: isDimmed ? 0.5 : 1,
            transition: "all 0.2s ease-in-out",
            "& .MuiAvatar-img": { objectFit: "contain" },
          }}
        />
        {healthStatus === "toWatch" ? (
          <Box
            sx={{
              position: "absolute",
              insetInlineEnd: -3,
              bottom: -2,
              width: markerStatusSize + 3,
              height: markerStatusSize + 2,
              clipPath: "polygon(50% 0, 100% 100%, 0 100%)",
              bgcolor: "common.white",
              filter: isDimmed ? "grayscale(1)" : "none",
              opacity: isDimmed ? 0.5 : 1,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: "1px",
                clipPath: "polygon(50% 0, 100% 100%, 0 100%)",
                bgcolor: healthColor,
              }}
            />
          </Box>
        ) : healthStatus === "unhealthy" ? (
          <Box
            sx={{
              position: "absolute",
              insetInlineEnd: -2,
              bottom: -2,
              width: markerStatusSize + 3,
              height: markerStatusSize + 3,
              borderRadius: "50%",
              bgcolor: healthColor,
              border: "1px solid white",
              filter: isDimmed ? "grayscale(1)" : "none",
              opacity: isDimmed ? 0.5 : 1,
            }}
          />
        ) : null}
      </Box>
    </Tooltip>
  );
});

const CommandCenterMapComponent: React.FC<Props> = ({
  customers,
  stores,
  selectedCustomerId,
  selectedStoreId,
  healthByStoreId,
  onSelectStore,
  layoutVersion,
  expanded,
}) => {
  const theme = useTheme();
  const isRtl = theme.direction === "rtl";
  const mapRef = React.useRef<MapRef>(null);
  const storeService = React.useMemo(() => StoreService.getInstance(), []);
  const userAdjustedViewport = React.useRef(false);
  const hasManuallyZoomed = React.useRef(false);
  const previousSelectedCustomerId = React.useRef(selectedCustomerId);
  const previousSelectedStoreId = React.useRef(selectedStoreId);
  const previousLayoutVersion = React.useRef(layoutVersion);
  const requestedFittingRoomCounts = React.useRef(new Set<number>());
  const [isMapLoaded, setIsMapLoaded] = React.useState(false);
  const { getStoreBranding } = useStoreBranding();
  const [fittingRoomCounts, setFittingRoomCounts] = React.useState<Record<number, number | null>>(
    {},
  );
  const mapHeight = expanded ? { xs: 500, md: 600 } : 400;
  const mappable = React.useMemo(
    () =>
      stores.filter(
        (store) => Number.isFinite(store.geoPoint?.lat) && Number.isFinite(store.geoPoint?.lon),
      ),
    [stores],
  );
  const markerSignature = React.useMemo(
    () =>
      mappable.map((store) => `${store.id}:${store.geoPoint.lon}:${store.geoPoint.lat}`).join(","),
    [mappable],
  );
  const customerNameById = React.useMemo(
    () => new globalThis.Map(customers.map((customer) => [customer.id, customer.name])),
    [customers],
  );
  const loadFittingRoomCount = React.useCallback(
    async (storeId: number) => {
      if (requestedFittingRoomCounts.current.has(storeId)) return;
      requestedFittingRoomCounts.current.add(storeId);
      const response = await storeService.getChangingRoomsByStore(storeId);
      const count = response.success
        ? (response.data ?? []).reduce(
            (total, group) =>
              total +
              group.changingRooms.filter((changingRoom) => changingRoom.type === "FITTING_ROOM")
                .length,
            0,
          )
        : null;
      setFittingRoomCounts((current) => ({ ...current, [storeId]: count }));
    },
    [storeService],
  );
  React.useEffect(() => {
    if (!isMapLoaded) return;
    const selected = mappable.find((store) => store.id === selectedStoreId);
    if (selected) {
      mapRef.current?.flyTo({
        center: [selected.geoPoint.lon, selected.geoPoint.lat],
        zoom: SELECTED_STORE_ZOOM,
      });
    }
  }, [isMapLoaded, selectedStoreId, mappable]);
  React.useEffect(() => {
    if (!isMapLoaded) return;
    const frame = requestAnimationFrame(() => mapRef.current?.resize());
    return () => cancelAnimationFrame(frame);
  }, [isMapLoaded, layoutVersion]);
  React.useEffect(() => {
    const layoutChanged = previousLayoutVersion.current !== layoutVersion;
    previousLayoutVersion.current = layoutVersion;
    if (!layoutChanged || !isMapLoaded || hasManuallyZoomed.current || !mappable.length) return;
    const frame = requestAnimationFrame(() => {
      const map = mapRef.current;
      if (!map) return;
      map.resize();
      if (mappable.length === 1) {
        map.flyTo({
          center: [mappable[0].geoPoint.lon, mappable[0].geoPoint.lat],
          zoom: 10,
          duration: 1000,
          essential: true,
        });
        return;
      }
      const bounds = turf.bbox(
        turf.featureCollection(
          mappable.map((store) => turf.point([store.geoPoint.lon, store.geoPoint.lat])),
        ),
      );
      map.fitBounds(
        [
          [bounds[0], bounds[1]],
          [bounds[2], bounds[3]],
        ],
        { padding: 64, duration: 1000, maxZoom: 10 },
      );
    });
    return () => cancelAnimationFrame(frame);
  }, [isMapLoaded, layoutVersion, markerSignature, mappable]);
  React.useEffect(() => {
    const isShowingAllStores = previousSelectedStoreId.current !== null && selectedStoreId === null;
    previousSelectedStoreId.current = selectedStoreId;
    if (isShowingAllStores) userAdjustedViewport.current = false;
  }, [selectedStoreId]);
  React.useEffect(() => {
    const customerChanged = previousSelectedCustomerId.current !== selectedCustomerId;
    previousSelectedCustomerId.current = selectedCustomerId;
    if (selectedCustomerId !== null && customerChanged) {
      userAdjustedViewport.current = false;
      hasManuallyZoomed.current = false;
    }
  }, [selectedCustomerId]);
  React.useEffect(() => {
    if (!isMapLoaded || selectedStoreId || userAdjustedViewport.current || !mappable.length) return;
    const map = mapRef.current;
    if (!map) return;
    if (mappable.length === 1) {
      map.flyTo({
        center: [mappable[0].geoPoint.lon, mappable[0].geoPoint.lat],
        zoom: 10,
        duration: 1000,
        essential: true,
      });
      return;
    }
    const bounds = turf.bbox(
      turf.featureCollection(
        mappable.map((store) => turf.point([store.geoPoint.lon, store.geoPoint.lat])),
      ),
    );
    map.fitBounds(
      [
        [bounds[0], bounds[1]],
        [bounds[2], bounds[3]],
      ],
      { padding: 64, duration: 1000, maxZoom: 10 },
    );
  }, [isMapLoaded, markerSignature, mappable, selectedStoreId]);
  const renderStore = (store: Store, interaction?: StackInteraction) => {
    const branding = getStoreBranding(store.customerId);
    return (
      <StoreMapMarker
        store={store}
        healthStatus={getMonitoringHealthStatus(healthByStoreId[store.id]?.health)}
        isDimmed={selectedStoreId !== null && selectedStoreId !== store.id}
        fittingRoomCount={fittingRoomCounts[store.id]}
        avatar={branding.avatar}
        color={branding.color}
        customerName={customerNameById.get(store.customerId) || store.customerName}
        expanded={expanded}
        onSelectStore={onSelectStore}
        loadFittingRoomCount={loadFittingRoomCount}
        interaction={interaction}
      />
    );
  };
  if (!token) {
    return (
      <Box
        sx={{ height: mapHeight, display: "grid", placeItems: "center", bgcolor: "action.hover" }}
      />
    );
  }
  return (
    <Box
      sx={{
        position: "relative",
        height: mapHeight,
        borderRadius: 2,
        overflow: "hidden",
        // Marker priorities stay below Mapbox controls; tooltips retain their portal layer.
        "& .mapboxgl-canvas-container": { isolation: "isolate" },
      }}
    >
      <Map
        ref={mapRef}
        mapboxAccessToken={token}
        initialViewState={{ longitude: 10, latitude: 35, zoom: 2 }}
        mapStyle="mapbox://styles/mapbox/light-v11"
        dragRotate={false}
        touchZoomRotate={false}
        renderWorldCopies={false}
        minZoom={1}
        maxZoom={CLUSTER_MAX_ZOOM + 1}
        reuseMaps
        onLoad={() => setIsMapLoaded(true)}
        onDragStart={() => {
          userAdjustedViewport.current = true;
        }}
        onZoomStart={(event) => {
          if ((event as unknown as { originalEvent?: Event }).originalEvent) {
            userAdjustedViewport.current = true;
            hasManuallyZoomed.current = true;
          }
        }}
      >
        <NavigationControl position={isRtl ? "top-left" : "top-right"} showCompass={false} />
        {isMapLoaded && (
          <CommandCenterMapMarkers
            customerNameById={customerNameById}
            stores={mappable}
            selectedStoreId={selectedStoreId}
            healthByStoreId={healthByStoreId}
            expanded={expanded}
            renderStore={renderStore}
          />
        )}
      </Map>
    </Box>
  );
};

export const CommandCenterMap = React.memo(
  CommandCenterMapComponent,
  (previous, next) =>
    previous.customers === next.customers &&
    previous.stores === next.stores &&
    previous.selectedCustomerId === next.selectedCustomerId &&
    previous.selectedStoreId === next.selectedStoreId &&
    previous.healthByStoreId === next.healthByStoreId &&
    previous.layoutVersion === next.layoutVersion &&
    previous.expanded === next.expanded &&
    previous.onSelectStore === next.onSelectStore,
);
